import { useEffect, useRef, useState } from 'react'
import CurrencyInput from 'react-currency-input-field'
import { Check, CircleNotch, WarningCircle } from '@phosphor-icons/react'
import './RateCell.css'

const CURRENCY_INPUT_PROPS = {
  className: 'form-control',
  decimalSeparator: '.',
  groupSeparator: ',',
  decimalsLimit: 2,
  style: { width: '140px' }
}

const SAVED_VISIBLE_MS = 1600

const toNumberOrNull = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

const sameRate = (a, b) => (a ?? null) === (b ?? null)

/**
 * A single currency input that autosaves on blur and tells the user, inline,
 * whether the value was stored. `onSave(nextValue)` must resolve to
 * `{ success: boolean, message?: string }`.
 */
const RateCell = ({
  id,
  ariaLabel,
  savedRate = null,
  fallbackRate = null,
  onSave,
  renderHint,
  unit = 'money'
}) => {
  const money = unit === 'money'
  const fieldProps = money
    ? { prefix: '$', placeholder: '$0.00' }
    : { suffix: 'h', placeholder: '0h' }

  const initial = savedRate ?? fallbackRate
  const [value, setValue] = useState(initial != null ? String(initial) : '')
  const [persisted, setPersisted] = useState(savedRate ?? null)
  const [status, setStatus] = useState('idle') // idle | saving | saved | error
  const [flash, setFlash] = useState(false)
  const savedTimer = useRef()

  useEffect(() => () => clearTimeout(savedTimer.current), [])

  const commit = async () => {
    let next = toNumberOrNull(value)
    // What rate is actually in effect right now (explicit override, else default).
    const effective = persisted ?? fallbackRate ?? null

    if (sameRate(next, effective) && status !== 'error') return

    // Typing the exact default value means "just use the default" -> clear the override.
    if (next != null && fallbackRate != null && next === Number(fallbackRate)) next = null

    setStatus('saving')
    const res = await onSave(next)

    if (res?.success) {
      setPersisted(next)
      if (next === null) setValue(fallbackRate != null ? String(fallbackRate) : '')
      setStatus('saved')
      setFlash(true)
      clearTimeout(savedTimer.current)
      savedTimer.current = setTimeout(() => {
        setStatus('idle')
        setFlash(false)
      }, SAVED_VISIBLE_MS)
    } else {
      setStatus('error')
    }
  }

  let tone = 'muted'
  let content = null

  if (status === 'saving') {
    content = (
      <>
        <CircleNotch className="rate-cell__spinner" size={12} weight="bold" />
        Saving…
      </>
    )
  } else if (status === 'saved') {
    tone = 'success'
    content = (
      <>
        <Check size={12} weight="bold" />
        Saved
      </>
    )
  } else if (status === 'error') {
    tone = 'danger'
    content = (
      <>
        <WarningCircle size={12} weight="bold" />
        Couldn&apos;t save ·{' '}
        <button type="button" className="rate-cell__retry" onClick={commit}>
          Retry
        </button>
      </>
    )
  } else if (renderHint) {
    content = renderHint({ persisted })
  }

  return (
    <span className="rate-cell">
      <span className="rate-cell__input-wrap" data-flash={flash}>
        <CurrencyInput
          id={id}
          name={id}
          aria-label={ariaLabel}
          value={value}
          onValueChange={(next) => setValue(next ?? '')}
          onBlur={commit}
          {...CURRENCY_INPUT_PROPS}
          {...fieldProps}
        />
      </span>
      <span className="rate-cell__status" data-tone={tone} role="status" aria-live="polite">
        {content && (
          <span key={`${status}-${tone}`} className="rate-cell__status-inner">
            {content}
          </span>
        )}
      </span>
    </span>
  )
}

export default RateCell
